import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AppContextService } from '../services/app-context.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { OperationResult } from '../models/server-result/operation-result';
import { InviteCompanyForNetworkTicketSearchCriteria } from '../models/invitations/company-invite-for-network-ticket-search-criteria';
import { InviteCompanyForNetworkTicketSearchData } from '../models/invitations/company-invite-for-network-ticket-search-data';
import { AcceptNewCompanyInviteForConfirmingNetwork } from '../models/invitations/confirming/accept-new-company-invite-confirming-network';
import { InviteNewCompanyForConfirmingNetwork } from '../models/invitations/confirming/invite-new-company-for-confirming-network';
import { RefuseNewCompanyInviteForConfirmingNetwork } from '../models/invitations/confirming/refuse-new-company-invite-confirming-network';
import { CreateCompanyInviteFlowCache } from '../models/invitations/create-company-invite-flow-cache';
import { GetCompanyInviteFlowCacheResult } from '../models/invitations/get-company-invite-flow-cache-result';
import { InviteUserInput } from '../models/invitations/invite-user-input';
import { PublicInviteCompanyForNetworkTicket } from '../models/invitations/public-invite-company-network-ticket';
import { PublicUserInvitationData } from '../models/invitations/public-user-invitation-data';
import { UpdateCompanyInvitationFlowCache } from '../models/invitations/update-company-invitation-flow-cache';
import { InviteNewSupplierForFactoringNetwork } from '../models/invitations/factoring/invite-new-supplier-for-factoring-network';
import { RefuseCompanyInviteForFactoringNetwork } from '../models/invitations/factoring/refuse-company-invite-factoring-network';
import { AcceptCompanyInviteForFactoringNetwork } from '../models/invitations/factoring/accept-company-invite-factoring-network';
import { ConfirmingValidateNewSupplierVatNumberResult } from '../models/invitations/confirming/confirming-validate-new-supplier-vat-number-result';

@Injectable()
export class InvitationsApiService {
	constructor(
		private http: HttpClient,
		private appContext: AppContextService,
		private domSanitizer: DomSanitizer
	) {}

	public userInvitation(inviteUserInput: InviteUserInput): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/newUser')!,
			inviteUserInput
		);
	}

	public createCompanyInvitationFlowCache(
		createCompanyInviteFlowCacheInput: CreateCompanyInviteFlowCache
	): Observable<OperationResult<string>> {
		return this.http.post<OperationResult<string>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/companyInvitationFlow')!,
			createCompanyInviteFlowCacheInput
		);
	}

	public getCachedCompanyInvitationFlow(invitationId: string): Observable<OperationResult<GetCompanyInviteFlowCacheResult>> {
		return this.http.get<OperationResult<GetCompanyInviteFlowCacheResult>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/companyInvitationFlow/' + invitationId)!
		);
	}

	public updateCachedCompanyInvitationFlow(
		updateCompanyInvitationFlowCache: UpdateCompanyInvitationFlowCache
	): Observable<OperationResult<Boolean>> {
		return this.http.put<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/companyInvitationFlow/' + updateCompanyInvitationFlowCache.InvitationId
			)!,
			updateCompanyInvitationFlowCache
		);
	}

	public createNewSupplierInviteForConfirmingNetwork(
		inviteNewCompanyForConfirmingNetwork: InviteNewCompanyForConfirmingNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/confirmingNetworkTicketForNewSuppplier')!,
			inviteNewCompanyForConfirmingNetwork
		);
	}

	public createNewBuyerInviteForConfirmingNetwork(
		inviteNewCompanyForConfirmingNetwork: InviteNewCompanyForConfirmingNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/confirmingNetworkTicketForNewBuyer')!,
			inviteNewCompanyForConfirmingNetwork
		);
	}

	public acceptNewSupplierInviteForConfirmingNetwork(
		acceptCompanyInviteForConfirmingNetwork: AcceptNewCompanyInviteForConfirmingNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/acceptNewSupplierForConfirmingNetworkInvitation'
			)!,
			acceptCompanyInviteForConfirmingNetwork
		);
	}

	public acceptNewBuyerInviteForConfirmingNetwork(
		acceptCompanyInviteForConfirmingNetwork: AcceptNewCompanyInviteForConfirmingNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/acceptNewBuyerForConfirmingNetworkInvitation'
			)!,
			acceptCompanyInviteForConfirmingNetwork
		);
	}

	public refuseNewCompanyInviteForConfirmingNetwork(
		refuseCompanyInviteForConfirmingNetwork: RefuseNewCompanyInviteForConfirmingNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/refuseNewSupplierForConfirmingNetworkInvitation'
			)!,
			refuseCompanyInviteForConfirmingNetwork
		);
	}

	public countSearchCompanyInviteForNetworkTicket(
		criteria: InviteCompanyForNetworkTicketSearchCriteria
	): Observable<OperationResult<number>> {
		return this.http.post<OperationResult<number>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/searchCount')!,
			criteria
		);
	}

	public searchCompanyInviteForNetworkTicket(
		criteria: InviteCompanyForNetworkTicketSearchCriteria
	): Observable<OperationResult<InviteCompanyForNetworkTicketSearchData[]>> {
		// On server the Page number is 0 based
		criteria.PageNumber -= 1;

		return this.http.post<OperationResult<InviteCompanyForNetworkTicketSearchData[]>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/search')!,
			criteria
		);
	}

	public getUserInvitation(
		tokenAssociatedEmail: string,
		userCompanyId: string,
		token: string
	): Observable<OperationResult<PublicUserInvitationData>> {
		const requestData = { TokenRelatedEmail: tokenAssociatedEmail, UserCompanyId: userCompanyId, Token: token };

		return this.http.post<OperationResult<PublicUserInvitationData>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/userInvitation/')!,
			requestData
		);
	}

	public resendInviteByInvitationId(invitationId: string): Observable<OperationResult<boolean>> {
		const requestData = {
			InvitationId: invitationId,
		};

		return this.http.post<OperationResult<boolean>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/resendInviteByInvitationId/')!,
			requestData
		);
	}

	public getCompanyInvitationTicket(invitationId: string, token: string): Observable<OperationResult<PublicInviteCompanyForNetworkTicket>> {
		const requestData = {
			InvitationId: invitationId,
			Token: token,
		};

		return this.http.post<OperationResult<PublicInviteCompanyForNetworkTicket>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/companyInvitationTicket/')!,
			requestData
		);
	}

	public createNewSupplierInviteForFactoringNetwork(
		inviteNewSupplierForFactoringNetwork: InviteNewSupplierForFactoringNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(SecurityContext.URL, this.appContext.getAPIUrl() + '/invitation/factoringNetworkTicketForNewSupplier')!,
			inviteNewSupplierForFactoringNetwork
		);
	}

	public refuseCompanyInviteForFactoringNetwork(
		refuseCompanyInviteForFactoringNetwork: RefuseCompanyInviteForFactoringNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/refuseCompanyForFactoringNetworkInvitation'
			)!,
			refuseCompanyInviteForFactoringNetwork
		);
	}

	public acceptSupplierInviteForFactoringNetwork(
		acceptCompanyInviteForFactoringNetwork: AcceptCompanyInviteForFactoringNetwork
	): Observable<OperationResult<Boolean>> {
		return this.http.post<OperationResult<Boolean>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/acceptSupplierForFactoringNetworkInvitation'
			)!,
			acceptCompanyInviteForFactoringNetwork
		);
	}

	public confirmingValidateNewSupplierVatNumber(
		invitingCompanyId: string,
		confirmingContractId: string,
		invitedCompanyVatNumber: string,
		invitedCompanyCountryCode: string
	): Observable<OperationResult<ConfirmingValidateNewSupplierVatNumberResult>> {
		const requestData = {
			InvitingCompanyId: invitingCompanyId,
			ConfirmingContractId: confirmingContractId,
			InvitedCompanyVatNumber: invitedCompanyVatNumber,
			InvitedCompanyCountryCode: invitedCompanyCountryCode,
		};

		return this.http.post<OperationResult<ConfirmingValidateNewSupplierVatNumberResult>>(
			this.domSanitizer.sanitize(
				SecurityContext.URL,
				this.appContext.getAPIUrl() + '/invitation/companies/confirming/validateNewSupplierVatNumber'
			)!,
			requestData
		);
	}
}
